import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../Context/AuthContext";
import logo from "../assets/vignan_online.png";

const UserInfo = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { token, user: currentUser, setUser } = useAuth();

  const [userInfo, setUserInfo] = useState(null);
  const [referrals, setReferrals] = useState([]);
  const [allUserReferrals, setAllUserReferrals] = useState([]);
  const [pointsData, setPointsData] = useState({ balance: 0, totalEarned: 0 });
  const [pointsHistory, setPointsHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("referrals");
  const [copiedEmail, setCopiedEmail] = useState("");
  const [pagination, setPagination] = useState({ page: 1, totalPages: 1, total: 0 });

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      console.log("Loading data for user ID:", id);

      try {
        // Fetch user details from referrals
        try {
          const userResp = await axios.get(
            `${process.env.REACT_APP_API_BASE_URL}/api/referrals/referrer/${id}`,
            { headers: { Authorization: `Bearer ${token}` } }
          );
          if (userResp.status === 200 && userResp.data.referrals) {
            console.log("Referrals data loaded:", userResp.data);

            // Extract user info from the first referral's referrer data
            if (userResp.data.referrals.length > 0 && userResp.data.referrals[0].referrer) {
              const referrerInfo = userResp.data.referrals[0].referrer;
              console.log("User details extracted from referrer:", referrerInfo);
              setUserInfo(referrerInfo);
            } else {
              // If no referrals, try to get user from /api/users endpoint
              try {
                const fallbackResp = await axios.get(
                  `${process.env.REACT_APP_API_BASE_URL}/api/users/${id}`,
                  { headers: { Authorization: `Bearer ${token}` } }
                );
                if (fallbackResp.status === 200) {
                  console.log("User details loaded from users endpoint:", fallbackResp.data);
                  setUserInfo(fallbackResp.data);
                }
              } catch (fallbackError) {
                console.error("Error fetching user from fallback endpoint:", fallbackError);
              }
            }
          }
        } catch (error) {
          console.error("Error fetching user details:", error);
        }

        // Fetch all referrals and filter by user
        try {
          const refResp = await axios.get(
            `${process.env.REACT_APP_API_BASE_URL}/api/referrals/all-data`,
            { headers: { Authorization: `Bearer ${token}` } }
          );

          if (refResp.status === 200 && refResp.data.referral) {
            const userReferrals = refResp.data.referral.filter(
              ref => ref.referrer && ref.referrer.id === parseInt(id)
            );
            console.log("User referrals loaded:", userReferrals.length);
            setAllUserReferrals(userReferrals);

            // Set initial pagination
            const totalReferrals = userReferrals.length;
            const totalPages = Math.ceil(totalReferrals / 10);
            const paginatedReferrals = userReferrals.slice(0, 10);

            setReferrals(paginatedReferrals);
            setPagination({
              page: 1,
              totalPages: totalPages,
              total: totalReferrals
            });
          }
        } catch (error) {
          console.error("Error fetching referrals:", error);
        }


        // Fetch points data
        try {
          console.log("Fetching points for user ID:", id);
          const pointsResp = await axios.get(
            `${process.env.REACT_APP_API_BASE_URL}/api/points/user/${id}`,
            { headers: { Authorization: `Bearer ${token}` } }
          );

          console.log("Points API full response:", pointsResp);
          console.log("Points API data:", pointsResp.data);

          if (pointsResp.status === 200 && pointsResp.data.status === "1") {
            const { balance, totalEarned, transactions } = pointsResp.data.data;
            console.log("✅ User points loaded successfully!");
            console.log("   - Balance:", balance);
            console.log("   - Total Earned:", totalEarned);
            console.log("   - Transactions count:", transactions?.length || 0);

            setPointsData({
              balance: balance || 0,
              totalEarned: totalEarned || 0
            });
            setPointsHistory(transactions || []);
          } else {
            console.warn("⚠️ Points API returned unexpected response:", pointsResp.data);
            setPointsData({ balance: 0, totalEarned: 0 });
            setPointsHistory([]);
          }
        } catch (error) {
          console.error("❌ Error fetching points data:", error);
          console.error("   Error message:", error.message);
          console.error("   Error response:", error.response?.data);
          console.error("   Error status:", error.response?.status);
          setPointsData({ balance: 0, totalEarned: 0 });
          setPointsHistory([]);
        }
      } catch (error) {
        console.error("Error loading data:", error);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [id, token]);

  // Handle pagination changes
  useEffect(() => {
    if (allUserReferrals.length > 0) {
      const startIndex = (pagination.page - 1) * 10;
      const endIndex = startIndex + 10;
      const paginatedReferrals = allUserReferrals.slice(startIndex, endIndex);
      setReferrals(paginatedReferrals);
    }
  }, [pagination.page, allUserReferrals]);

  const handleCopyEmail = (email) => {
    if (!email) return;
    navigator.clipboard.writeText(email);
    setCopiedEmail(email);
    setTimeout(() => setCopiedEmail(""), 2000);
  };

  const handleNextPage = () => {
    if (pagination.page < pagination.totalPages) {
      setPagination((prev) => ({ ...prev, page: prev.page + 1 }));
    }
  };

  const handlePrevPage = () => {
    if (pagination.page > 1) {
      setPagination((prev) => ({ ...prev, page: prev.page - 1 }));
    }
  };

  const signout = () => {
    localStorage.removeItem("user");
    localStorage.removeItem("token");
    setUser(null);
    navigate("/", { replace: true });
  };

  const formatDate = (utcTimestamp) => {
    if (!utcTimestamp) return "N/A";
    const date = new Date(utcTimestamp);
    return date.toLocaleString('en-IN', {
      timeZone: 'Asia/Kolkata',
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
    });
  };

  // Calculate stats from all referrals
  const stats = {
    total: allUserReferrals.length || 0,
    enrolled: allUserReferrals.filter(r => r.status?.toLowerCase() === 'enrolled').length || 0,
    inProgress: allUserReferrals.filter(r => r.status?.toLowerCase() === 'inprogress').length || 0,
    rejected: allUserReferrals.filter(r => r.status?.toLowerCase() === 'rejected').length || 0,
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#004f6b]"></div>
          <p className="text-gray-600 font-medium">Loading user details...</p>
        </div>
      </div>
    );
  }

  if (!userInfo) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-600 font-medium mb-4">User not found</p>
          <button
            onClick={() => navigate('/sales')}
            className="px-6 py-2 bg-[#004f6b] text-white rounded-lg font-bold"
          >
            Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col font-sans">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white border-b border-gray-100 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            <div className="flex items-center gap-10">
              <div className="flex-shrink-0 cursor-pointer" onClick={() => navigate('/sales')}>
                <img src={logo} alt="Vignan Online" className="h-10 w-auto object-contain" />
              </div>
              <div className="hidden lg:flex items-center gap-4 py-2 px-5 bg-gray-50 rounded-2xl border border-gray-100">
                <div className="h-10 w-10 flex items-center justify-center bg-[#004f6b] text-white rounded-xl font-bold text-lg shadow-inner">
                  {currentUser.first_name?.charAt(0)}
                </div>
                <div className="flex flex-col text-start">
                  <p className="text-sm font-bold text-gray-900 leading-none">{currentUser.first_name}</p>
                  <p className="text-[10px] font-bold text-[#004f6b] uppercase tracking-widest mt-1.5 opacity-80">{currentUser.user_type} Portal</p>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-6">

              <button
                onClick={signout}
                className="group flex items-center gap-3 px-5 py-2.5 text-sm font-bold text-gray-600 hover:text-red-600 bg-gray-50 hover:bg-red-50 rounded-xl border border-gray-200 hover:border-red-100 transition-all active:scale-95 shadow-sm"
              >
                <span>Logout</span>
                <svg className="w-4 h-4 transform group-hover:translate-x-1 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-grow py-8 bg-gray-50/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <button
            onClick={() => navigate('/sales', { state: { activeTab: 12 } })}
            className="px-5 py-2.5 mb-5 text-sm font-bold text-gray-600 hover:text-[#004f6b] bg-gray-50 hover:bg-gray-100 rounded-xl border border-gray-200 transition-all active:scale-95 shadow-sm flex items-center gap-2"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Back to Users
          </button>

          {/* User Profile Card */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 mb-8 overflow-hidden">
            <div className="bg-gradient-to-r from-[#004f6b] to-[#006b8f] p-8">
              <div className="flex items-center gap-6">
                <div className="h-24 w-24 rounded-full bg-white/20 backdrop-blur-sm border-4 border-white/30 flex items-center justify-center text-white font-bold text-4xl shadow-xl">
                  {userInfo?.first_name?.charAt(0) || 'U'}
                </div>
                <div className="flex-1">
                  <h1 className="text-3xl  text-start font-bold text-white mb-2">
                    {userInfo?.first_name} {userInfo?.last_name || ''}
                    <span className="px-3 py-1 bg-white/20 backdrop-blur-sm rounded-full text-xs font-bold uppercase tracking-wider">
                      {userInfo?.user_type}
                    </span>
                  </h1>
                  <div className="flex items-center gap-4 text-white/90">
                    <span
                      className="flex items-center gap-2 cursor-pointer hover:text-white transition-colors"
                      onClick={() => handleCopyEmail(userInfo?.email)}
                    >
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                      </svg>
                      {userInfo?.email}
                    </span>
                    {userInfo?.phone_number && (
                      <span className="flex items-center gap-2">
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                        </svg>
                        {userInfo.phone_number}
                      </span>
                    )}

                  </div>
                </div>
              </div>
            </div>

            {/* Stats Grid */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6 p-8 bg-gray-50/50">
              <div className="bg-white rounded-xl p-6 border border-gray-200 shadow-sm">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-bold text-gray-400 uppercase tracking-widest">Total Referrals</span>
                  <div className="p-2 bg-blue-50 rounded-lg">
                    <svg className="w-5 h-5 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                    </svg>
                  </div>
                </div>
                <p className="text-3xl font-bold text-gray-900">{stats.total}</p>
              </div>

              <div className="bg-white rounded-xl p-6 border border-gray-200 shadow-sm">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-bold text-gray-400 uppercase tracking-widest">Enrolled</span>
                  <div className="p-2 bg-green-50 rounded-lg">
                    <svg className="w-5 h-5 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                </div>
                <p className="text-3xl font-bold text-green-600">{stats.enrolled}</p>
              </div>

              <div className="bg-white rounded-xl p-6 border border-gray-200 shadow-sm">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-bold text-gray-400 uppercase tracking-widest">In Progress</span>
                  <div className="p-2 bg-amber-50 rounded-lg">
                    <svg className="w-5 h-5 text-amber-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                </div>
                <p className="text-3xl font-bold text-amber-600">{stats.inProgress}</p>
              </div>

              <div className="bg-white rounded-xl p-6 border border-gray-200 shadow-sm">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-bold text-gray-400 uppercase tracking-widest">Wallet Balance</span>
                  <div className="p-2 bg-purple-50 rounded-lg">
                    <svg className="w-5 h-5 text-purple-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                </div>
                <p className="text-3xl font-bold text-purple-600">{pointsData.balance} pts</p>
              </div>
            </div>
          </div>

          {/* Tabs */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
            <div className="border-b border-gray-200">
              <div className="flex gap-1 p-2">
                <button
                  onClick={() => setActiveTab("referrals")}
                  className={`flex-1 px-6 py-3 rounded-lg font-bold text-sm transition-all ${activeTab === "referrals"
                    ? "bg-[#004f6b] text-white shadow-md"
                    : "text-gray-600 hover:bg-gray-50"
                    }`}
                >
                  Referrals
                </button>
                <button
                  onClick={() => setActiveTab("wallet")}
                  className={`flex-1 px-6 py-3 rounded-lg font-bold text-sm transition-all ${activeTab === "wallet"
                    ? "bg-[#004f6b] text-white shadow-md"
                    : "text-gray-600 hover:bg-gray-50"
                    }`}
                >
                  Wallet & Points
                </button>
              </div>
            </div>

            {/* Referrals Tab */}
            {activeTab === "referrals" && (
              <div>
                {/* Pagination Controls */}
                <div className="p-4 border-b border-gray-100 bg-gray-50/50 flex items-center justify-between">
                  <div className="text-sm text-gray-500">
                    {pagination.total > 0 ? (
                      <>
                        Showing <span className="font-bold text-gray-900">{(pagination.page - 1) * 10 + 1}</span> to{" "}
                        <span className="font-bold text-gray-900">
                          {Math.min(pagination.page * 10, pagination.total)}
                        </span>{" "}
                        of <span className="font-bold text-gray-900">{pagination.total}</span> referrals
                      </>
                    ) : (
                      <>
                        Page <span className="font-bold text-gray-900">{pagination.page || 1}</span> of{" "}
                        <span className="font-bold text-gray-900">{pagination.totalPages || 1}</span>
                      </>
                    )}
                  </div>
                  <div className="flex items-center gap-3">
                    <button
                      onClick={handlePrevPage}
                      disabled={pagination.page <= 1}
                      className="px-4 py-2 bg-white border border-gray-200 rounded-lg text-sm font-bold text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-sm flex items-center gap-2"
                    >
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                      </svg>
                      Prev
                    </button>
                    <button
                      onClick={handleNextPage}
                      disabled={pagination.page >= pagination.totalPages}
                      className="px-4 py-2 bg-white border border-gray-200 rounded-lg text-sm font-bold text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-sm flex items-center gap-2"
                    >
                      Next
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </button>
                  </div>
                </div>

                <div className="overflow-x-auto">
                  <table className="w-full text-left border-collapse">
                    <thead>
                      <tr className="bg-gray-50 border-b border-gray-200">
                        <th className="px-4 py-4 text-[10px] font-bold text-gray-600 uppercase tracking-widest text-center">Sr. No</th>
                        <th className="px-4 py-4 text-[10px] font-bold text-gray-600 uppercase tracking-widest">Referee Name</th>
                        <th className="px-4 py-4 text-[10px] font-bold text-gray-600 uppercase tracking-widest">Email</th>
                        <th className="px-4 py-4 text-[10px] font-bold text-gray-600 uppercase tracking-widest">Program</th>
                        <th className="px-4 py-4 text-[10px] font-bold text-gray-600 uppercase tracking-widest">Status</th>
                        <th className="px-4 py-4 text-[10px] font-bold text-gray-600 uppercase tracking-widest">Added On</th>
                        <th className="px-4 py-4 text-[10px] font-bold text-gray-600 uppercase tracking-widest">Updated On</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {referrals.length > 0 ? (
                        referrals.map((referral, index) => (
                          <tr key={index} className="hover:bg-gray-50/50 transition-colors">
                            <td className="px-4 py-4 text-sm text-gray-400 font-mono text-center">
                              {(pagination.page - 1) * 10 + index + 1}
                            </td>
                            <td className="px-4 py-4 font-semibold text-gray-800">{referral?.candidate_name}</td>
                            <td className="px-4 py-4">
                              <span
                                className="text-sm text-gray-600 hover:text-[#004f6b] cursor-pointer flex items-center gap-1"
                                onClick={() => handleCopyEmail(referral?.candidate_email)}
                              >
                                {referral?.candidate_email}
                              </span>
                            </td>
                            <td className="px-4 py-4">
                              <span className="px-2 py-1 bg-blue-50 text-blue-700 rounded text-[10px] font-bold border border-blue-100 uppercase">
                                {referral?.candidate_program || 'N/A'}
                              </span>
                            </td>
                            <td className="px-4 py-4">
                              <span className={`inline-flex items-center px-3 py-1 rounded-full text-[10px] font-bold tracking-wider border
                                                                ${referral?.status?.toLowerCase() === 'enrolled' ? 'bg-green-50 text-green-700 border-green-200' :
                                  referral?.status?.toLowerCase() === 'rejected' ? 'bg-red-50 text-red-700 border-red-200' :
                                    'bg-amber-50 text-amber-700 border-amber-200'}`}>
                                {referral?.status ? referral.status.toUpperCase() : 'UNKNOWN'}
                              </span>
                            </td>
                            <td className="px-4 py-4 text-sm text-gray-600">{formatDate(referral?.created_at)}</td>
                            <td className="px-4 py-4 text-sm text-gray-600">{formatDate(referral?.updated_at)}</td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan="7" className="px-4 py-20 text-center">
                            <div className="flex flex-col items-center justify-center space-y-3">
                              <div className="bg-gray-50 p-4 rounded-full">
                                <svg className="w-12 h-12 text-gray-200" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
                                </svg>
                              </div>
                              <span className="text-gray-400 text-sm font-medium">No referrals found.</span>
                            </div>
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Wallet & Points Tab */}
            {activeTab === "wallet" && (
              <div className="p-8">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
                  <div className="bg-gradient-to-br from-[#004f6b] to-[#006b8f] rounded-2xl p-8 text-white shadow-xl">
                    <div className="flex items-center justify-between mb-4">
                      <span className="text-sm font-bold uppercase tracking-wider opacity-90">Available Balance</span>
                      <svg className="w-8 h-8 opacity-80" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z" />
                      </svg>
                    </div>
                    <p className="text-5xl font-bold mb-2">{pointsData.balance}</p>
                    <p className="text-sm opacity-80">Reward Points</p>
                  </div>

                  <div className="bg-gradient-to-br from-[#006b8f] to-[#008fb8] rounded-2xl p-8 text-white shadow-xl">
                    <div className="flex items-center justify-between mb-4">
                      <span className="text-sm font-bold uppercase tracking-wider opacity-90">Total Earned</span>
                      <svg className="w-8 h-8 opacity-80" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                      </svg>
                    </div>
                    <p className="text-5xl font-bold mb-2">{pointsData.totalEarned}</p>
                    <p className="text-sm opacity-80">Lifetime Points</p>
                  </div>
                </div>

                {/* Points History */}
                <div className="bg-white rounded-xl border border-gray-200">
                  <div className="p-6 border-b border-gray-200">
                    <h3 className="text-lg font-bold text-gray-900">Points History</h3>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full text-left border-collapse">
                      <thead>
                        <tr className="bg-gray-50 border-b border-gray-200">
                          <th className="px-6 py-4 text-[10px] font-bold text-gray-600 uppercase tracking-widest">Date</th>
                          <th className="px-6 py-4 text-[10px] font-bold text-gray-600 uppercase tracking-widest">Description</th>
                          <th className="px-6 py-4 text-[10px] font-bold text-gray-600 uppercase tracking-widest">Type</th>
                          <th className="px-6 py-4 text-[10px] font-bold text-gray-600 uppercase tracking-widest text-right">Points</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {pointsHistory.length > 0 ? (
                          pointsHistory.map((history, index) => {
                            const type = history?.transaction_type?.toLowerCase();
                            const isCredit = type === 'credited' || type === 'credit' || type === 'earned' || type === 'refund';
                            return (
                              <tr key={index} className="hover:bg-gray-50/50 transition-colors">
                                <td className="px-6 py-4 text-sm text-gray-600">{formatDate(history?.created_at)}</td>
                                <td className="px-6 py-4 text-sm text-gray-800 font-medium">
                                  {history?.description || history?.remarks || 'Points Transaction'}
                                </td>
                                <td className="px-6 py-4">
                                  <span className={`px-2 py-1 rounded text-[10px] font-bold uppercase ${isCredit ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'
                                    }`}>
                                    {history?.transaction_type || 'N/A'}
                                  </span>
                                </td>
                                <td className={`px-6 py-4 text-right font-bold ${isCredit ? 'text-green-600' : 'text-red-600'
                                  }`}>
                                  {isCredit ? '+' : '-'}{history?.points || 0}
                                </td>
                              </tr>
                            );
                          })
                        ) : (
                          <tr>
                            <td colSpan="4" className="px-6 py-20 text-center">
                              <div className="flex flex-col items-center justify-center space-y-3">
                                <div className="bg-gray-50 p-4 rounded-full">
                                  <svg className="w-12 h-12 text-gray-200" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                                  </svg>
                                </div>
                                <span className="text-gray-400 text-sm font-medium">No transaction history available.</span>
                              </div>
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
};

export default UserInfo;